import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/Navbar";
import { Lock, Mail, ArrowRight, Sparkles } from "lucide-react";
import { trackEvent } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/member/login`, { email, password });
      // First ever member login = "activated" for our funnel
      // (we don't have self-serve sign up; admin creates the account after their trial call)
      const firstLoginKey = `first_login_${res.data.member.id}`;
      if (!localStorage.getItem(firstLoginKey)) {
        trackEvent("member_activated", {
          content_name: "Member Portal Activation",
          status: true,
        });
        localStorage.setItem(firstLoginKey, "1");
      }
      localStorage.setItem("member_token", res.data.token);
      localStorage.setItem("member_name", res.data.member.name);
      toast.success(`Welcome back, ${res.data.member.name.split(" ")[0]}!`);
      navigate("/member/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid="member-login-page">
      <Navbar />
      <section className="hero-gradient dark:bg-transparent pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto animate-fade-in-up">
          <div className="text-center mb-8">
            <Badge className="bg-brand-purple-soft text-brand-purple mb-4 font-body font-semibold px-4 py-1.5 rounded-full">
              <Sparkles className="inline h-3.5 w-3.5 mr-1" /> Member Portal
            </Badge>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">Welcome back</h1>
            <p className="text-slate-600 dark:text-slate-400 font-body">Sign in to book your weekly classes for free.</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-5" data-testid="member-login-form">
            <div>
              <Label htmlFor="m-email" className="font-body text-sm text-slate-700 dark:text-slate-200 mb-1.5 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="m-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="pl-10 rounded-xl font-body" placeholder="you@example.com" data-testid="member-input-email" />
              </div>
            </div>
            <div>
              <Label htmlFor="m-pw" className="font-body text-sm text-slate-700 dark:text-slate-200 mb-1.5 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="m-pw" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="pl-10 rounded-xl font-body" placeholder="••••••••" data-testid="member-input-password" />
              </div>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full rounded-full bg-brand-purple hover:bg-brand-purple-hover text-white font-body font-bold py-6"
              data-testid="member-submit-btn">
              {loading ? "Signing in..." : <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>

            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 text-center space-y-2">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-body">
                New here?{" "}
                <Link to="/member/signup" className="text-brand-gold font-semibold hover:underline" data-testid="login-signup-link">
                  Create an account
                </Link>
                {" "}— it's free
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 font-body">
                Or{" "}
                <Link to="/enrol" className="text-brand-purple font-semibold hover:underline" data-testid="member-trial-link">
                  enrol for next term
                </Link>
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
